import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Pencil, Trash2, Search, MapPin, Globe } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export const AdminTrainingCitiesPage = () => {
  const { t, language } = useLanguage();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    country: '',
    country_ar: '',
    is_active: true,
  });

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchCities = async () => {
    try {
      const response = await fetch(`${API}/admin/training-cities`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCities(data);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      toast.error('Failed to load training cities');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openDialog = (city = null) => {
    if (city) {
      setEditingCity(city);
      setFormData({
        name: city.name || '',
        name_ar: city.name_ar || '',
        country: city.country || '',
        country_ar: city.country_ar || '',
        is_active: city.is_active !== false,
      });
    } else {
      setEditingCity(null);
      setFormData({
        name: '',
        name_ar: '',
        country: '',
        country_ar: '',
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingCity 
        ? `${API}/admin/training-cities/${editingCity.city_id}`
        : `${API}/admin/training-cities`;
      
      const response = await fetch(url, {
        method: editingCity ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(editingCity ? 'City updated successfully' : 'City created successfully');
        setDialogOpen(false);
        fetchCities();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to save city');
      }
    } catch (error) {
      toast.error('Failed to save city');
    }
  };

  const handleDelete = async (cityId) => {
    if (!window.confirm('Are you sure you want to delete this city?')) return;

    try {
      const response = await fetch(`${API}/admin/training-cities/${cityId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('City deleted successfully');
        fetchCities();
      } else {
        toast.error('Failed to delete city');
      }
    } catch (error) {
      toast.error('Failed to delete city');
    }
  };

  const filteredCities = cities.filter(city => 
    city.name.toLowerCase().includes(search.toLowerCase()) ||
    city.name_ar.includes(search) ||
    (city.country && city.country.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="p-6" data-testid="admin-training-cities-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">Training Cities</h1>
          <p className="text-[#6B7280]">Manage cities where training is offered</p>
        </div>
        <Button className="btn-primary" onClick={() => openDialog()} data-testid="add-city-btn">
          <Plus className="h-4 w-4 me-2" />
          Add City
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="relative">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
            <Input
              placeholder="Search cities..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ps-9 max-w-sm"
              data-testid="search-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33AADD] mx-auto"></div>
            </div>
          ) : filteredCities.length === 0 ? (
            <div className="text-center py-8">
              <MapPin className="h-12 w-12 text-[#E5E7EB] mx-auto mb-3" />
              <p className="text-[#6B7280]">No training cities found</p>
              <p className="text-sm text-[#9CA3AF]">Add cities where you offer training programs</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>City (English)</TableHead>
                    <TableHead>City (Arabic)</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-end">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCities.map((city, index) => (
                    <TableRow key={city.city_id} data-testid={`city-row-${index}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-[#33AADD]" />
                          <span className="font-medium text-[#111827]">{city.name}</span>
                        </div>
                      </TableCell>
                      <TableCell dir="rtl">{city.name_ar}</TableCell>
                      <TableCell>
                        {city.country && (
                          <div className="flex items-center gap-1 text-[#6B7280]">
                            <Globe className="h-3 w-3" />
                            {language === 'ar' ? city.country_ar || city.country : city.country}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={city.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {city.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-end">
                        <Button variant="ghost" size="sm" onClick={() => openDialog(city)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(city.city_id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* City Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCity ? 'Edit' : 'Add'} Training City</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">City Name (English) *</Label>
                <Input 
                  id="name" 
                  value={formData.name} 
                  onChange={(e) => handleChange('name', e.target.value)} 
                  placeholder="e.g., Berlin"
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name_ar">City Name (Arabic) *</Label>
                <Input 
                  id="name_ar" 
                  value={formData.name_ar} 
                  onChange={(e) => handleChange('name_ar', e.target.value)} 
                  placeholder="مثال: برلين"
                  dir="rtl"
                  required 
                />
              </div>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country">Country (English)</Label>
                <Input 
                  id="country" 
                  value={formData.country} 
                  onChange={(e) => handleChange('country', e.target.value)} 
                  placeholder="e.g., Germany"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country_ar">Country (Arabic)</Label>
                <Input 
                  id="country_ar" 
                  value={formData.country_ar} 
                  onChange={(e) => handleChange('country_ar', e.target.value)} 
                  placeholder="مثال: ألمانيا"
                  dir="rtl"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="is_active" 
                checked={formData.is_active} 
                onChange={(e) => handleChange('is_active', e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="is_active">Active (visible in dropdowns)</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTrainingCitiesPage;
